import { useState, useEffect } from 'react'
import axios from 'axios'
import { DateTime } from 'luxon'
import { makeRequest } from 'services/request.service'
import { useAuth } from 'auth/AuthContext'
import {
  buildDashboardDateRangeQuery,
  createDefaultDateRange,
  readStoredDashboardDateRange,
  writeStoredDashboardDateRange
} from 'utils/dateRangeFilters'

// GET request for workday summary data
const getWorkdaySummaryData = async (farmId, dateRange, signal) => {
  try {
    const response = await makeRequest({
      method: 'GET',
      url: `/dashboard/${farmId}/workday-summary?${buildDashboardDateRangeQuery(dateRange)}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      },
      signal
    })
    return response.data
  } catch (error) {
    console.error('Error fetching workday summary data:', error)
    throw error
  }
}

export const useWorkdaySummary = (customFarmId = null, initialDate = null) => {
  const { user } = useAuth()

  // Use custom farmId if provided, otherwise fall back to user.farmId
  const farmId = customFarmId || user?.farmId

  const initialFilterDate = initialDate || DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd')
  const initialDateRange = readStoredDashboardDateRange(initialFilterDate)

  const [workdaySummary, setWorkdaySummary] = useState({
    farmId,
    totalWorkers: 0,
    workersByGroup: [],
    workersInBlock: [],
    farmActivityData: [],
    lastActivitiesData: [],
    filterDate: initialDateRange.dateFrom,
    dateRange: initialDateRange,
    currentDate: DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd'),
    loading: true,
    error: null
  })

  const updateFilterDate = (newDate) => {
    const nextDateRange = createDefaultDateRange(newDate)
    writeStoredDashboardDateRange(nextDateRange)
    setWorkdaySummary(prev => ({
      ...prev,
      filterDate: newDate,
      dateRange: nextDateRange
    }))
  }

  const updateDateRange = (dateRange) => {
    writeStoredDashboardDateRange(dateRange)
    setWorkdaySummary(prev => ({
      ...prev,
      filterDate: dateRange.dateFrom,
      dateRange
    }))
  }

  // Fetch on mount and whenever farmId or filterDate changes.
  // AbortController cancels the in-flight request on cleanup (handles React StrictMode double-invoke).
  useEffect(() => {
    if (!farmId) return
    const controller = new AbortController()

    const doFetch = async () => {
      try {
        setWorkdaySummary(prev => ({ ...prev, loading: true, error: null }))
        const data = await getWorkdaySummaryData(farmId, workdaySummary.dateRange, controller.signal)
        setWorkdaySummary(prev => ({
          ...prev,
          farmId,
          farmActivityData: data.farmActivitySummary || [],
          lastActivitiesData: data.lastActivitiesBoard || [],
          workersByGroup: data.workersByGroup || [],
          workersInBlock: data.workersInBlock || [],
          totalWorkers: data.totalActiveWorkers || 0,
          loading: false
        }))
      } catch (error) {
        if (axios.isCancel(error)) return
        console.error('Error fetching workday summary:', error)
        setWorkdaySummary(prev => ({ ...prev, loading: false, error: 'Failed to load workday summary data' }))
      }
    }

    doFetch()
    return () => controller.abort()
  }, [farmId, workdaySummary.dateRange])

  return {
    data: workdaySummary,
    updateFilterDate,
    updateDateRange
  }
}
